<template>
  <Grid gtr="auto 1fr" class="custom-meta-data full-height">
    <Grid align-content="start">
      <Grid gaf="column" justify-content="space-between" align-items="center">
        <h3 class="text-subtitle-1 text-secondary-1">Custom metadata</h3>

        <template v-if="metaDataValues && !noMetadata">
          <CloseIcon v-if="editMode" class="close-icon" @click="editMode = false" />
          <EditIcon v-else class="cursor-pointer" with-hover @click.native="editMode = true" />
        </template>
      </Grid>

      <slot name="below-title"></slot>
    </Grid>

    <Grid v-if="!metaDataValues" justify-content="center" align-items="center">
      <q-spinner color="primary" size="2.5em" />
    </Grid>

    <Grid v-else-if="noMetadata" place-content="center" place-items="center">
      <NoData />
      <span>There are no metadata created yet</span>
    </Grid>

    <Grid
      v-else
      justify-content="space-between"
      align-content="start"
      gtc="repeat(auto-fit, minmax(200px, 300px))"
      gap="var(--r-space-md)"
      class="fields q-mt-sm"
    >
      <div v-for="(fields, categoryName) in metaDataValues" :key="categoryName" class="field-wrapper">
        <div class="text-body-3 text-secondary-1">{{ categoryName }}</div>
        <q-separator></q-separator>

        <Grid class="q-mt-md" gap="var(--r-space-sm)">
          <template v-for="(field, index) of fields">
            <template v-if="editMode">
              <RInput
                v-if="field.type.name === 'text'"
                :key="field.id"
                v-model="field.values"
                :label="field.name"
                :hint="field.description"
                no-feedback-icon
                :no-hint="!field.description"
                @blur="updateValues(field, index, categoryName)"
              />

              <RDropdown
                v-if="field.type.name === 'select'"
                :key="field.id"
                :value="field.values ? field.values.filter(val => !!val) : []"
                :label="field.name"
                :hint="field.description"
                :options="field.value_options"
                :no-hint="!field.description"
                multiple
                @input="field.values = $event"
                @blur="updateValues(field, index, categoryName)"
              />

              <div v-if="field.type.name === 'checkbox'" :key="field.id" class="text-secondary-1">
                <div class="text-body-1">{{ field.name }}:</div>

                <q-radio
                  v-model="field.values"
                  dense
                  :val="true"
                  class="q-mt-sm"
                  @input="updateValues(field, index, categoryName)"
                >
                  <div :class="field.values === true ? 'text-body-4' : 'text-body-1'">Yes</div>
                </q-radio>

                <q-radio
                  v-model="field.values"
                  dense
                  :val="false"
                  class="q-mt-sm q-ml-lg"
                  @input="updateValues(field, index, categoryName)"
                >
                  <div :class="field.values === false ? 'text-body-4' : 'text-body-1'">No</div>
                </q-radio>

                <RHint v-if="field.description">{{ field.description }}</RHint>
              </div>

              <div v-if="field.type.name === 'textarea'" :key="field.id">
                <RLabel>{{ field.name }}:</RLabel>

                <q-input
                  v-model="field.values"
                  type="textarea"
                  dense
                  autogrow
                  outlined
                  hide-hint
                  hide-bottom-space
                  @blur="updateValues(field, index, categoryName)"
                />

                <RHint v-if="field.description">{{ field.description }}</RHint>
              </div>
            </template>

            <template v-else>
              <div :key="field.id" class="field text-secondary-1">
                <div class="text-body-1">{{ field.name }}</div>
                <div class="text-body-3 q-mt-xs">{{ valueDisplay(field.values) }}</div>
              </div>
            </template>
          </template>
        </Grid>
      </div>
    </Grid>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import { cloneDeep } from 'lodash'
import hash from 'object-hash'
// Components
import EditIcon from '@/ui/icons/EditIcon'
import RLabel from '@/ui/shared/RLabel'
import RHint from '@/ui/shared/RHint'
import CloseIcon from '@/ui/icons/CloseIcon'
import NoData from '@/reusable/svg-icons/NoData'

export default {
  components: {
    EditIcon,
    RLabel,
    RHint,
    CloseIcon,
    NoData
  },

  props: {
    entityId: {
      type: [Number, String],
      required: true
    },

    entityName: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      editMode: false,
      metaDataValues: null,
      metaDataValuesOriginal: null
    }
  },

  computed: {
    noMetadata() {
      return Array.isArray(this.metaDataValues) && !this.metaDataValues.length
    }
  },

  watch: {
    entityId: {
      handler: 'fetchData',
      immediate: true
    }
  },

  methods: {
    valueDisplay(val) {
      if ([true, false].includes(val)) return val ? 'Yes' : 'No'
      if (!val) return 'N/A'
      if (Array.isArray(val) && !val.length) return 'N/A'
      if (Array.isArray(val)) return val.join(', ')

      return val
    },

    fetchData() {
      apiTo.GET_METADATA_VALUES_FOR_ENTITY(this.entityName, this.entityId).then(({ data }) => {
        this.metaDataValues = cloneDeep(data.result)
        this.metaDataValuesOriginal = data.result
      })
    },

    updateValues(field, fieldIndex, categoryName) {
      const valuesHash = hash(field.values)
      const originalHash = hash(this.metaDataValuesOriginal[categoryName][fieldIndex]['values'])

      if (valuesHash === originalHash) return

      let newValue = field.values

      if (field.type.name === 'select') {
        if (!newValue.length) newValue = null
        else newValue = newValue.filter(val => !!val)
      } else if (['text', 'textarea'].includes(field.type.name)) {
        if (!newValue) newValue = null
      }

      apiTo
        .UPDATE_METADATA_VALUE_FOR_ENTITY(field.id, { values: newValue }, this.entityName, this.entityId)
        .finally(this.fetchData)
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-meta-data {
  .q-radio {
    ::v-deep .q-radio__inner {
      color: var(--r-secondary-1);
    }
  }
}
</style>
